import { useState } from 'react';
import { Loader2, MessageCircle } from 'lucide-react';
import {
  api,
  type PortfolioAskCitation,
  type PortfolioAskContext,
  type ScreeningRequest,
} from '../services/api';
import { colors } from '../lib/theme';

const EXAMPLE_PROMPTS = [
  'Why was LMT excluded?',
  "What's my defense exposure?",
  'Which holdings need review?',
  'How is my compliance score calculated?',
];

interface PortfolioAskPanelProps {
  context: PortfolioAskContext;
  filters: ScreeningRequest['filters'];
  onCitationClick?: (symbol: string) => void;
}

export function PortfolioAskPanel({
  context,
  filters,
  onCitationClick,
}: PortfolioAskPanelProps) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [citations, setCitations] = useState<PortfolioAskCitation[]>([]);
  const [latencyMs, setLatencyMs] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  async function submitQuestion(prompt: string) {
    const trimmed = prompt.trim();
    if (trimmed.length < 10) {
      setError('Please enter a question (at least 10 characters).');
      return;
    }

    setLoading(true);
    setError(null);
    setAnswer(null);
    setCitations([]);
    setUnavailable(false);

    try {
      const response = await api.askPortfolio(trimmed, {
        rows: context.rows,
        portfolio: context.portfolio,
        filters,
      });
      setAnswer(response.answer);
      setCitations(response.citations);
      setLatencyMs(response.latencyMs);
      setQuestion(trimmed);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (message.includes('not enabled') || message.includes('503')) {
        setUnavailable(true);
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitQuestion(question);
  }

  if (unavailable) {
    return null;
  }

  return (
    <div
      className="mt-6 rounded-2xl p-5"
      style={{
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bgTertiary,
      }}
    >
      <div className="flex items-center gap-2 text-sm font-medium" style={{ color: colors.textPrimary }}>
        <MessageCircle className="h-4 w-4" style={{ color: colors.blue }} />
        Ask about your portfolio
      </div>
      <p className="mt-1 text-xs" style={{ color: colors.textMuted }}>
        AI answers based on your screening results and our methodology. Not investment advice.
      </p>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="e.g. Why was LMT flagged for defense?"
          maxLength={500}
          className="flex-1 rounded-xl px-4 py-2.5 text-sm outline-none"
          style={{
            backgroundColor: colors.bgSecondary,
            border: `1px solid ${colors.border}`,
            color: colors.textPrimary,
          }}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || question.trim().length < 10}
          className="rounded-xl px-5 py-2.5 text-sm font-medium transition-opacity disabled:opacity-50"
          style={{ backgroundColor: colors.blue, color: '#fff' }}
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Asking…
            </span>
          ) : (
            'Ask'
          )}
        </button>
      </form>

      <div className="mt-3 flex flex-wrap gap-2">
        {EXAMPLE_PROMPTS.map((prompt) => (
          <button
            key={prompt}
            type="button"
            onClick={() => {
              setQuestion(prompt);
              submitQuestion(prompt);
            }}
            disabled={loading}
            className="rounded-full px-3 py-1 text-xs transition-opacity hover:opacity-80 disabled:opacity-50"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`,
              color: colors.textSecondary,
            }}
          >
            {prompt}
          </button>
        ))}
      </div>

      {error && (
        <p className="mt-3 text-xs" style={{ color: colors.red }}>
          {error}
        </p>
      )}

      {answer && (
        <div
          className="mt-4 rounded-xl p-4"
          style={{
            backgroundColor: `${colors.blue}15`,
            border: `1px solid ${colors.blue}40`,
          }}
        >
          <p className="text-sm leading-relaxed" style={{ color: colors.textPrimary }}>
            {answer}
          </p>
          {latencyMs != null && (
            <p className="mt-2 text-[10px]" style={{ color: colors.textMuted }}>
              Answered in {(latencyMs / 1000).toFixed(1)}s
            </p>
          )}
          {citations.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {citations.map((c, i) => (
                <button
                  key={`${c.symbol ?? 'ctx'}-${c.lens ?? i}`}
                  type="button"
                  onClick={() => c.symbol && onCitationClick?.(c.symbol)}
                  className="rounded-lg px-2 py-1 text-[10px] text-left transition-opacity hover:opacity-80"
                  style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`,
                    color: colors.textSecondary,
                  }}
                >
                  {c.symbol ?? 'Context'}
                  {c.lens ? ` · ${c.lens}` : ''}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
